import { Icon } from 'vopay';
import { Trans, useTranslation } from 'react-i18next';
import { Card, Button, Title, BodyText } from 'UI';
import styles from './SubAccountsWorkflowDialog.module.scss';

type RailStats = { created: number; replaced: number };

type SubAccountsWorkflowDialogProps = {
  perRail: Record<string, RailStats>;
  failedPermission: number;
  totalSubAccounts: number;
  onConfirm: (value: boolean) => void;
  onCancel: () => void;
};

export default function SubAccountsWorkflowDialog({
  perRail,
  failedPermission,
  totalSubAccounts,
  onConfirm,
  onCancel,
}: SubAccountsWorkflowDialogProps) {
  const { t } = useTranslation('translations', {
    keyPrefix: 'TransactionApproval.Configuration.Workflows.Update.SubAccountsDialog',
  });

  const appliedCount = totalSubAccounts - failedPermission;
  const addedRails = Object.entries(perRail).filter(([, s]) => s.created > 0);
  const replacedRails = Object.entries(perRail).filter(([, s]) => s.replaced > 0);

  return (
    <Card hoverEffect={false} className={styles.dialogCard}>
      <div className={styles.iconWrapper}>
        <Icon icon="dialogSuccess" size="xxlg" width="60px" height="60px" color="none" />
      </div>

      <Title level="4" className={styles.title}>
        {t('title', { defaultValue: 'Workflow Created' })}
      </Title>

      <BodyText className={`${styles.grey} ${styles.centered}`}>
        <Trans
          i18nKey="TransactionApproval.Configuration.Workflows.Update.SubAccountsDialog.subtitle"
          ns="translations"
          values={{ appliedCount, totalSubAccounts }}
          components={{ bold: <strong className={styles.black} /> }}
        />
      </BodyText>

      {addedRails.length > 0 && (
        <div className={styles.section}>
          <Title level="6">{t('added.title', { defaultValue: 'Workflow(s) added' })}</Title>
          <BodyText size="sm" className={`${styles.grey} ${styles.sectionDescription}`}>
            {t('added.description', { defaultValue: 'The inherited workflow was added to these sub-account(s).' })}
          </BodyText>
          <table className={`${styles.table} ${styles.sectionTable}`}>
            <thead>
              <tr>
                <th>
                  <BodyText size="sm" weight="md" className={styles.black}>
                    {t('table.rail', { defaultValue: 'Payment Rail(s)' })}
                  </BodyText>
                </th>
                <th>
                  <BodyText size="sm" weight="md" className={styles.black}>
                    {t('table.subAccounts', { defaultValue: 'Sub-Account(s)' })}
                  </BodyText>
                </th>
              </tr>
            </thead>
            <tbody>
              {addedRails.map(([rail, stats]) => (
                <tr key={rail}>
                  <td>
                    <BodyText size="sm" className={styles.grey}>
                      {rail}
                    </BodyText>
                  </td>
                  <td>
                    <BodyText size="sm" className={styles.grey}>
                      {stats.created}
                    </BodyText>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {replacedRails.length > 0 && (
        <div className={styles.section}>
          <Title level="6">{t('replaced.title', { defaultValue: 'Workflow(s) replaced' })}</Title>
          <BodyText size="sm" className={`${styles.grey} ${styles.sectionDescription}`}>
            {t('replaced.description', {
              defaultValue:
                'Existing workflows were replaced. In-flight Pending Approval transactions have been affected.',
            })}
          </BodyText>
          <table className={`${styles.table} ${styles.sectionTable}`}>
            <thead>
              <tr>
                <th>
                  <BodyText size="sm" weight="md">
                    {t('table.rail', { defaultValue: 'Payment Rail(s)' })}
                  </BodyText>
                </th>
                <th>
                  <BodyText size="sm" weight="md">
                    {t('table.subAccounts', { defaultValue: 'Sub-Account(s)' })}
                  </BodyText>
                </th>
              </tr>
            </thead>
            <tbody>
              {replacedRails.map(([rail, stats]) => (
                <tr key={rail}>
                  <td>
                    <BodyText size="sm">{rail}</BodyText>
                  </td>
                  <td>
                    <BodyText size="sm">{stats.replaced}</BodyText>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {failedPermission > 0 && (
        <BodyText size="sm" className={`${styles.grey} ${styles.centered}`}>
          <Trans
            i18nKey="TransactionApproval.Configuration.Workflows.Update.SubAccountsDialog.failed"
            ns="translations"
            values={{ failedPermission }}
            components={{ bold: <strong className={styles.black} /> }}
          />
        </BodyText>
      )}

      <div className={styles.buttonGroup}>
        <Button colorScheme="primary" size="small" onClick={() => onConfirm(true)}>
          {t('assignApprovers', { defaultValue: 'Assign Approvers' })}
        </Button>
        <Button colorScheme="secondary" size="small" onClick={onCancel}>
          {t('close', { defaultValue: 'Close' })}
        </Button>
      </div>
    </Card>
  );
}
